import fs from 'node:fs'
import path from 'node:path'
import { Readable } from 'node:stream'
import ignore from 'ignore'
import tar from 'tar-fs'

import type { GithubProject } from '@scalar-org/entities/project'
import type { VersionedScalarConfig } from '@scalar-org/scalar-config'

import { apiService } from '@/helpers'

const DEFAULT_IGNORED_FILES = ['node_modules', '.git']

function isErrnoException(error: unknown): error is NodeJS.ErrnoException {
  return error instanceof Error && 'code' in error
}

/** Read content from .gitignore */
async function readGitIgnore(configDir: string) {
  try {
    const gitignore = await fs.promises.readFile(
      path.resolve(configDir, '.gitignore'),
      'utf8',
    )
    return gitignore
  } catch (err) {
    if (isErrnoException(err) && err.code === 'ENOENT') return null
    throw err
  }
}

/** Creates a tarball of the specified configDir and returns it as a stream */
async function createProjectTar(configDir: string) {
  const ignorer = ignore({ allowRelativePaths: true })

  const gitignore = await readGitIgnore(configDir)

  if (gitignore) ignorer.add(gitignore)

  ignorer.add(DEFAULT_IGNORED_FILES)

  return Readable.from(
    tar.pack(configDir, {
      ignore: (name) => ignorer.ignores(name),
    }),
  )
}

export async function publishGithub({
  slug,
  preview,
  apiClient,
}: {
  slug: string
  preview?: boolean
  apiClient: Awaited<ReturnType<typeof apiService>>
}) {
  const publishGithubResult = await apiClient.publish.publishGithub({
    slug,
    preview,
  })

  if (publishGithubResult.error) {
    return {
      success: false as const,
      error: publishGithubResult.message,
    }
  }

  return {
    success: true as const,
    publishUid: publishGithubResult.data.publishUid,
  }
}

/** Collect all the files and their contents specified in a v2 config and publish them. */
export async function publishProject({
  configDir,
  configPath,
  config,
  gitProject,
  preview,
}: {
  configDir: string
  configPath: string
  config: VersionedScalarConfig
  gitProject: GithubProject
  preview?: boolean
}) {
  try {
    // Resolve the root of the project
    const root = path.join(configDir, config.root ?? './')

    // The config path relative to the root of the uploaded project
    const relativeConfigPath = path.relative(root, configPath)

    const apiClient = await apiService()
    // Create a tar stream of the configDir
    const projectTar = await createProjectTar(root)

    // Send the tarball stream to publish-deploy
    const result = await apiClient.publish.publishCli({
      tarStream: Readable.toWeb(projectTar) as ReadableStream,
      slug: gitProject.slug,
      configPath: relativeConfigPath,
      preview,
    })

    if (result.error)
      return {
        success: false,
        error: result.originalError ?? result.message,
      } as const

    return { success: true, publishUid: result.data.publishUid } as const
  } catch (e) {
    return { success: false, error: e } as const
  }
}
